import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';
import {filter, map, merge, tap, withLatestFrom, takeUntil} from 'rxjs/operators';
import {selectActiveWorkflow ,selectRequestedRevision, selectTestElapsedTime} from "@ispm/workflow/store/workflow-config.selectors";
import {selectUiConfiguration} from "../../../configuration/store/configuration.selector";
import _, {has, cloneDeep} from 'lodash';
import { Subject } from 'rxjs';
import { WorkflowBarItem } from '@ispm/shared/store/shared.reducer';
import {WorkflowService} from "@ispm/workflow/services/workflow.service";
import {PractitionerService} from "@ispm/migration/services/practitioner.service";
import {AberrationService} from "@ispm/migration/services/aberration.service";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";


@Component({
  selector: 'gen-custom-workflow-bar',
  templateUrl: './custom-workflow-bar.component.html',
  styleUrls: ['./custom-workflow-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomWorkflowBarComponent implements OnInit, OnDestroy {

  private store: any;
  private inputs;
  private outputs;
  private stateParams;
  private _alive = true;
  private destroy$ = new Subject();
  private secondOpinioner: { value: string; };
  private statusIsAfterInitialResults: any;

  public steps$: Observable<Array<any>>;
  public activeIndex: number;
  public revision;
  public testElapsedTime;
  public stepTagline;
  public showSteps = false;
  public currTest;
  public hasSecondOpinion;
  public uiRelevantStatuses = ['review', '2nd opinion requested', '2nd opinion review', 'initial results', 'interpretation results'];
  public uiRelevantJobs = ['reviewTestResults', 'reviewResults', '2ndOpinionRequested', '2ndOpinionReview', 'initialResults', 'postTrials', 'TherapyMatch', 'InterpretationResults', 'therapymatchreflex', 'clinicalrialmatchreflex', 'clinicaltrialmatchreflex'];
  public uiActiveJobs: any = {};
  @Input() stepTaglineField: any;
  @Input() patientAnalyses: any;
  @Input() currentRevision;
  @Output()
  public onNotSupported: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private _changeDetector: ChangeDetectorRef,
              store: Store<IgenomicsState>,
              private workflowService: WorkflowService,
              private practitionerService: PractitionerService,
              private aberrationService: AberrationService,
              private routerManagerService: RouterManagerService) {
    this.store = store;
    this.steps$ = store.pipe(
      select(selectActiveWorkflow),
      merge(
        store.pipe(select('shared'))
      ),
      withLatestFrom(
        store.pipe<any>(select('shared')),
        store.pipe<any>(select(selectTestElapsedTime)),
        store.pipe(select(selectUiConfiguration('workflowVisibility.barItems', 'Test'))),
        store.pipe(select(selectRequestedRevision))
      ),
      map(([streamSource, workflowBarOverride, testElapsedTime, items, revision]) => {
        const emptyState = {items: null, status: null};
        this.revision = cloneDeep(revision);
        workflowBarOverride = cloneDeep(workflowBarOverride);
        items = cloneDeep(items);
        this.testElapsedTime = parseInt(testElapsedTime, 10);
        if (!(revision && items)) {
          return emptyState;
        } else if (!has(revision, 'ui.workflowVisibility.barItems')) {
          this.emitForNotSupported(true);
          return emptyState;
        } else if (workflowBarOverride.workflow_bar) {
          // in case of state override by ui actions (mostly used by revise for preview)
          return {
            items: workflowBarOverride.workflow_bar,
            status: workflowBarOverride.status
          };
        } else {
          return {
            items: this.formatItems(items, revision),
            status: revision.status
          };
        }
      }),
      filter(({items}) => !!items),
      tap(() => this.emitForNotSupported(false)),
      map(({items, status}) => {
        if (items && items.length > 0) {
          if (items.length == 1) {
            items[0].singleStep = true;
          }
          items[0].pos = 'first';
          items[items.length - 1].pos = 'last';
          this.activeIndex = items.findIndex((e) => e.isActive);
          if (!!status && items[this.activeIndex].fromStatus) {
            items[this.activeIndex].name = status;
          }
        }
        return items;
      }),
      tap(() => {
        if (this._alive) {
          this._changeDetector.markForCheck();
          this._changeDetector.detectChanges();
        }
      }),
      takeUntil(this.destroy$)
    );

  }

  ngOnInit() {
    if (this.stepTaglineField) {
      this.store
        .pipe(
          select((state: any) => state.tests.diagnosticOrders.order[this.stepTaglineField]),
          takeUntil(this.destroy$)
        )
        .subscribe((status) => {
          this.stepTagline = status;
          this._changeDetector.markForCheck();
          this._changeDetector.detectChanges();
        });
    }
      this.showSteps = true;
      this.stateParams = this.routerManagerService.parameters;
      this.uiActiveJobs.afterReview = ['reviewTestResults', '2ndOpinionRequested', '2ndOpinionReview', 'initialResults', 'postTrials', 'TherapyMatch', 'InterpretationResults', 'Final Review'];
      this.uiActiveJobs.betweenInitialAndFinalResults = ['postTrials', 'TherapyMatch'];
      this.uiActiveJobs.afterInitialResults = ['initialResults', 'postTrials', 'TherapyMatch', 'interpretationResults', 'none', "UiManualAberrationCuration", "Final review"];
      this.currTest = this.patientAnalyses.current.workflowRequest;
      this.setCurrentInputsAndOutputs();
      this.checkTestHasSecondOpinion();
      this.secondOpinioner = _.find(this.patientAnalyses.current.workflowRequest.references,  (reference) => {
        if (reference.type === "practitioner") {
          let refValue = JSON.parse(reference.value)
          return refValue.practType === '2ndOpinion';
        }
      });
      if (this.secondOpinioner) {
        let refValue = JSON.parse(this.secondOpinioner.value);
        this.practitionerService.get(refValue.practId)
          .pipe(
            takeUntil(this.destroy$)
          )
          .subscribe((res) => {
            this.secondOpinioner = res.body;
            this.aberrationService.setSecondOpinioner(this.secondOpinioner);
          })
      }
      this.isJobAfterInitialResults();
  }

  private formatItems(uiItems: WorkflowBarItem[], revision, keepItem = false) {
    if (!uiItems) { return null; }
    return  uiItems.filter((uiItem) => {
      if (uiItem.isActive) {
        if (revision.humanReadableMessage) {
          uiItem.tooltip = revision.humanReadableMessage;
        }
        if (['canceled', 'failed', 'cancelled', 'stopped'].includes(revision.status.toLowerCase())) {
          uiItem.name = revision.status;
          uiItem.fromStatus = true;
        }
      }
      if (uiItem.conditionPrevStepDone) {
        for (const revItem of revision.items) {
          if (revItem.name === uiItem.conditionPrevStepDone) {
            if (revItem.status.toLowerCase() !== "done")
              return false;
            if (!keepItem)
              delete uiItem.conditionPrevStepDone;
            break;
          }
        }
      }
      return true;
    });
  }

  private emitForNotSupported(status: boolean) {
    this.onNotSupported.emit(status);
  }

  private isTestCancelled() {
    return ['canceled', 'cancelled'].indexOf(this.currentRevision.status.toLowerCase()) > -1;
  }

  private isTestFailed() {
    return ['failed'].indexOf(this.currentRevision.status.toLowerCase()) > -1;
  }

  private isTestCompleted() {
    return ['test completed'].indexOf(this.currentRevision.status.toLowerCase()) > -1;
  }

  private setCurrentInputsAndOutputs() {
    let currentStep;
    for (let item of this.currentRevision.items) {
      if (item.name === this.currentRevision.activeJob.name) {
        currentStep = item;
        this.inputs = currentStep.inputs;
        this.outputs = currentStep.outputs;
      }
    }
  }

  private checkTestHasSecondOpinion() {
    this.workflowService.testHasSecondOpinion(this.stateParams.revisionId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        (opinions: any) => {
          if (opinions.data) {
            let secondOpinionOp = _.find(opinions.data, (op) => {
              return op.isSecondOpinion;
            })
            this.hasSecondOpinion = !!secondOpinionOp;
          } else {
            this.hasSecondOpinion = false;
          }
        },
        () => {
          this.hasSecondOpinion = false;
        }
    )
    .add(() => {
      this.hasSecondOpinion = this.hasSecondOpinion || ['2ndopinionrequested', '2ndopinionreview'].indexOf(this.currentRevision.activeJob.name.toLowerCase()) > -1;
    })
  }

  private isJobAfterInitialResults() {
    this.statusIsAfterInitialResults = this.uiActiveJobs.afterInitialResults?.find(job => {
      return job.toLowerCase() === this.currTest.activeJobName.toLowerCase();
    });
    this.aberrationService.setStatusIsAfterInitialResults(this.statusIsAfterInitialResults);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._changeDetector.detach();
    this._alive = false;
  }

  public trackByFn(item, index) {
    return index;
  }

  public stepInItems(stepType) {
    stepType = stepType.toLowerCase();
    return this.currentRevision.items.find(item => item.type.toLowerCase() === stepType)
  }

  public showCancelledStatus(name) {
    return this.currentRevision.activeJob && name.toLowerCase().split(',').indexOf(this.currentRevision.activeJob.name.toLowerCase()) > -1 && this.isTestCancelled();
  }

  public getStatus(stepType) {
    stepType = stepType.toLowerCase();
    let item = this.currentRevision.items.find(item => item.type.toLowerCase() === stepType);
    return item && item.initialStatusMsg && item.initialStatusMsg.toLowerCase() === 'therapy review' ? item.initialStatusMsg : 'Interpretation results';
  }

  public isAllowedStep(activeJob) {
    let isStepAllowed = false;
    let isTestNotCompleted = this.currentRevision.status.toLowerCase() != 'test completed';
    activeJob = activeJob.toLowerCase();
    switch (activeJob) {
      case "beforereview":
        isStepAllowed = !this.uiActiveJobs.afterReview.find((job) => {
          return job.toLowerCase() === this.currentRevision.activeJob.name.toLowerCase();
        });
        break;
      case "betweeninitialandfinalresults":
        isStepAllowed = this.uiActiveJobs.betweenInitialAndFinalResults.find((job) => {
          return job.toLowerCase() === this.currentRevision.activeJob.name.toLowerCase();
        })
        isStepAllowed = isStepAllowed || (this.currentRevision.activeJob.name.toLowerCase() == "initialresults" && this.currTest.status.toLowerCase() == 'done');
        break;
      case "final review":
        isStepAllowed = this.currentRevision.activeJob.name.toLowerCase() === activeJob && this.currTest.status.toLowerCase() == 'done' && this.inputs && this.inputs[0].key === 'prioritySet';
        break;
      case "reviewtestresults":
      case "2ndopinionrequested":
      case "2ndopinionreview":
      case "interpretationresults":
        isStepAllowed = (this.currentRevision.activeJob.name.toLowerCase() === activeJob) && (this.currTest.status.toLowerCase() == 'done');
        break;
      case "after review":
        isStepAllowed = (this.currentRevision.activeJob.name.toLowerCase() === activeJob.toLowerCase()) && (this.currTest.status.toLowerCase() == 'done');
        break;
    }
    return isStepAllowed && isTestNotCompleted;
  }

  public isActiveStep(activeJob) {
    activeJob = activeJob.toLowerCase();
    let isStepActive = false;
    let isJobActive = this.currentRevision.activeJob && this.currentRevision.activeJob.name.toLowerCase() == activeJob.toLowerCase();
    const isTestNotCompleted = !(this.isTestCancelled() || this.isTestCompleted() || this.isTestFailed());
    switch (activeJob) {
      case "reviewtestresults":
      case "2ndopinionrequested":
      case "2ndopinionreview":
      case "initialresults":
      case "testinforeview":
      case "interpretationresults":
        let isResultsTestStatusNotDone = this.currTest.status.toLowerCase() != 'done';
        isStepActive = isJobActive && isTestNotCompleted && isResultsTestStatusNotDone;
        break;
      case "uigeneralinput":
      case "reviewqcresults":
      case "pipelinefailed":
        let isPiplineTestStatusNotDone = this.currTest.status.toLowerCase() != 'done';
        isStepActive = this.currentRevision.activeJob.name.toLowerCase().includes(activeJob) && isTestNotCompleted && isPiplineTestStatusNotDone;
        break;
      case "final review":
        let isFinalTestStatusNotDone = this.currTest.status.toLowerCase() != 'done';
        isStepActive = isJobActive && isTestNotCompleted && isFinalTestStatusNotDone;
        break;
      case "testcompleted":
        isStepActive = !isTestNotCompleted;
        break;

      case 'uiselectpathway':
      case 'therapyprioritization':
      case 'collectdata':
      case 'completeplan':
      case 'documentspreview':
        let isTestStatusNotDone = this.currTest.status.toLowerCase() != 'done';
        isStepActive = isJobActive && isTestNotCompleted && isTestStatusNotDone;
        break;
    }
    return isStepActive;
  }
}

